import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {BooksRestService} from "../../service/books-service";
import {
  BookPageSelection,
  BookRendererControllerService,
  BookSelection
} from "../../service/book-renderer-controller.service";
import {Subscription} from "rxjs";
import {
  Bookmark,
  Dict,
  HotkeyEvent,
  HotkeysService,
  JumpTo,
  OpenChartOnFocusedBreakpoint,
  OpenFirstChart,
  Reading,
  ScaleBookFontSize,
  SwitchBookPage
} from "../../service/hotkeys.service";
import {BookmarksService, JumpKind} from "../../service/bookmarks.service";

@Component({
  selector: 'app-book-controller',
  templateUrl: './book-controller.component.html',
  styleUrls: ['./book-controller.component.css']
})
export class BookControllerComponent implements OnInit, OnDestroy {
  private lastPageRequested: number = null;
  private availableCourses = [];
  private readonly EN3_ONLINE_COURSE_CODE = "en3-online";
  private readonly EN3_COURSE_CODE = "en3";

  @Input()
  participantUuid: string;

  _currentPage = 0
  bookPageChangeSubscription: Subscription;
  private selection: BookPageSelection;
  private pageShared: BookPageSelection;
  sharedPageSubscription: Subscription;

  get currentPage() {
    return this._currentPage
  }

  private rendererSubscription: Subscription;
  constructor(private bookRendererControllerService: BookRendererControllerService,
              private booksRestService: BooksRestService,
              private hotkeysService: HotkeysService,
              private bookmarks: BookmarksService
              ) {
    this.bookPageChangeSubscription = bookRendererControllerService.currentBookPageSelection.subscribe(
      selection => {
        if (selection) {
          this.selection = selection;
          this.lastPageRequested = selection.page;
          if (selection.page !== this._currentPage) {
            this._currentPage = selection.page;
          }
        }
      }
    );
    this.sharedPageSubscription = this.bookRendererControllerService.bookPageShared
      .subscribe(pageShared =>
      this.pageShared = pageShared);
    this.hotkeysService.hotkeyEventSubject.subscribe( event => this.handleHotKeyEvent(event));
  }

  ngOnInit() {
    this.booksRestService.listProducts(this.participantUuid).subscribe(courses => this.availableCourses = courses || []);
  }

  ngOnDestroy(): void {
    if (this.rendererSubscription) this.rendererSubscription.unsubscribe();
    if (this.bookPageChangeSubscription) this.bookPageChangeSubscription.unsubscribe();
    if (this.sharedPageSubscription) this.sharedPageSubscription.unsubscribe();
  }

  onClose() {
    this._currentPage = 0;
    this.bookRendererControllerService.close();
  }

  hasPageRequested() {
    return this.lastPageRequested;
  }

  onMoveBreakpoint(offset: number) {
    if (!this.hasPageRequested()) return;
    if (!this.bookRendererControllerService.moveBreakpoint(offset)) {
      if (!this.findBookSelectionByPageNumber(this.lastPageRequested + offset)) return;
      if (offset > 0) this.bookRendererControllerService.moveToFirstBreakpointAfterPageLoad();
      else if (this.lastPageRequested + offset > 0) {
        this.bookRendererControllerService.moveToLastBreakpointAfterPageLoad();
      }
      this.onMovePage(offset);
    }
  }

  onMovePage(offset: number) {
    if (this.hasPageRequested() && this.lastPageRequested + offset > 0) {
      const differentBookSelection = this.prepareSelectionIfBookHasToBeChanged(this.lastPageRequested + offset);
      if (differentBookSelection) {
        this.bookRendererControllerService.openBookPage(differentBookSelection);
      } else {
        this.bookRendererControllerService.jumpToPage(this.lastPageRequested + offset);
      }
    }
  }

  private handleHotKeyEvent(event: HotkeyEvent) {
    if (event instanceof SwitchBookPage) {
      //this.onMovePage(event.offset);
      this.onMoveBreakpoint(event.offset);
    } else if (event instanceof OpenChartOnFocusedBreakpoint) {
      this.callForNextChartOnBreakpoint();
    }
  }

  hasPageShared() {
    return this.pageShared;
  }

  private callForNextChartOnBreakpoint() {
    if (!this.bookRendererControllerService.switchChartOnFocusedBreakpoint()) {
      this.hotkeysService.hotkeyEventSubject.next(new OpenFirstChart());
    }
  }

  private prepareSelectionIfBookHasToBeChanged(targetPageNumber: number) {
    const targetBookSelection = this.findBookSelectionByPageNumber(targetPageNumber);
    if (!targetBookSelection) return null;
    if (targetBookSelection.course.code !== this.selection.book.course.code
      || targetBookSelection.stage.code !== this.selection.book.stage.code) {
      return new BookPageSelection(targetBookSelection, targetPageNumber, 1);
    }
    return null;
  }

  private findBookSelectionByPageNumber(targetPageNumber: number): BookSelection {
    const foundInCurrentBook = this.selection.book.stage["content-items"].find( item => item["page-from"] <= targetPageNumber && item["page-to"] >= targetPageNumber);

    if (foundInCurrentBook) return this.selection.book;
    if (this.selection.book.stage.code.endsWith("exams")) return null;

    const stageInCurrentCourse = this.selection.book.course.stages
      .find(stage => stage["content-items"].find(item => item["page-from"] <= targetPageNumber && item["page-to"] >= targetPageNumber));
    if (stageInCurrentCourse) return new BookSelection(this.selection.book.course, stageInCurrentCourse);

    const isEn3OnlineStage = this.selection.book.course.code === this.EN3_ONLINE_COURSE_CODE
      && this.selection.book.stage.code.endsWith("_online");
    if (!isEn3OnlineStage) return null;

    const regularEn3Course = this.availableCourses.find(course => course.code === this.EN3_COURSE_CODE);
    if (!regularEn3Course || !regularEn3Course.stages) return null;

    const stageInRegularCourse = regularEn3Course.stages
      .find(stage => stage["content-items"].find(item => item["page-from"] <= targetPageNumber && item["page-to"] >= targetPageNumber));
    return stageInRegularCourse ? new BookSelection(regularEn3Course, stageInRegularCourse) : null;
  }

  isRegularBookmark() {
    return this.bookmarks.hasBookmark() && this.bookmarks.getBookmarkMode() === JumpKind.TEXT;
  }

  isActionBookmark() {
    return this.bookmarks.hasBookmark() && this.bookmarks.getBookmarkMode() !== JumpKind.TEXT;
  }

  getActionName() {
    return this.bookmarks.getBookmarkMode().actionName;
  }

  openBookmark() {
    this.hotkeysService.hotkeyEventSubject.next(new Bookmark());
  }

  getBookmarkPage() {
    return this.bookmarks.getBookmarkPage();
  }

  openJump() {
    this.hotkeysService.hotkeyEventSubject.next(new JumpTo());
  }

  openReading() {
    this.hotkeysService.hotkeyEventSubject.next(new Reading());
  }

  openDictation() {
    this.hotkeysService.hotkeyEventSubject.next(new Dict());
  }

  increaseFont() {
    this.hotkeysService.hotkeyEventSubject.next(new ScaleBookFontSize(1));
  }

  decreaseFont() {
    this.hotkeysService.hotkeyEventSubject.next(new ScaleBookFontSize(-1));
  }

  nextPage() {
    this.hotkeysService.hotkeyEventSubject.next(new SwitchBookPage(1));
  }
  previousPage() {
    this.hotkeysService.hotkeyEventSubject.next(new SwitchBookPage(-1));
  }
}
